import React, { useEffect, useRef, useState } from "react";
import "./quoteInvoiceEditModal.css";
import "../CreateQuoteModal/createQuoteModal.css";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import NewQuoteItemBox from "../CreateQuoteModal/NewQuoteItemBox";
import { useMutation } from "@apollo/client";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import {
  QUOTE_STATUS_OPTIONS,
  DISCOUNT_PERCENT_OPTIONS,
} from "../shared/quoteOptions";
import CreateQuoteValidator from "../../../../utils/formValidators/createQuoteValidator";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import NumberStepper from "../NumberStepper/NumberStepper";
import { arraysEqual } from "../../../../utils/comparators";
import { UPDATE_QUOTE_INVOICE } from "../../../../utils/GraphQL/mutations";
import PayPeriodMoveConfirmModal, { isNeedsPeriodConfirm } from "../PayPeriodMoveConfirmModal/PayPeriodMoveConfirmModal";
import SpecialRefinisherPayBlock, {
  buildSpecialRefinisherPayInput,
  validateSpecialRefinisherPay,
} from "../../../../components/SpecialRefinisherPayBlock/SpecialRefinisherPayBlock";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";

const clearedForm = {
  quoteStatus: "Active",
  dateCreated: "",
  createdBy: "",
  items: [],
  quoteTotal: "0",
  subtotal: "",
  customerName: "",
  quoteNum: "",
  expirationDate: "",
  discountPercent: "10",
  quoteNote: "",
  note: "",
  quoteType: "",
  customerId: "",
  assigneeId: "22",
  assigneeName: "All",
  splitBetween: [],
};

const clearedUpdates = {
  date: false,
  items: false,
  status: false,
  discount: false,
  total: false,
};

const QuoteInvoiceEditPage = ({
  selectedObj,
  isInvoice,
  handleSnack,
  returnNewObj,
  closeModal,
}) => {
  const [quoteForm, setQuoteForm] = useState({ ...clearedForm });
  const [quoteDate, setQuoteDate] = useState("");
  const [quoteDateMills, setQuoteDateMills] = useState("0");
  const [jobEndTimeMills, setJobEndTimeMills] = useState("");
  const [formErrors, setFormErrors] = useState([]);
  const [formUpdates, setFormUpdates] = useState({ ...clearedUpdates });
  const [discountTotal, setDiscountTotal] = useState("0");
  const [docNum, setDocNum] = useState("0");
  const [splitMin, setSplitMin] = useState("");
  const [splitMax, setSplitMax] = useState("");
  const [srpEnabled, setSrpEnabled] = useState(false);
  const [srpAmount, setSrpAmount] = useState(null);
  const [srpPayType, setSrpPayType] = useState(null);
  // Dirty-gate: only send specialRefinisherPay when the user touched the SRP
  // block THIS session. Omitting the arg preserves the server's stored value
  // (updateQuoteInvoice only writes it when explicitly passed) — prevents a
  // stale seed from `selectedObj` wiping an override set elsewhere (e.g. on
  // the calendar event) when saving an unrelated invoice edit.
  const srpDirtyRef = useRef(false);
  const messageInput = useRef();
  const { confirm } = useConfirm();
  const [updateGQLCall] = useMutation(UPDATE_QUOTE_INVOICE);
  // Pay-period move confirm (Option 2): set when editing an invoice's date would
  // re-bucket an approved job's paycheck across a settled period.
  const [periodMove, setPeriodMove] = useState(null);

  const quoteValidator = new CreateQuoteValidator(quoteForm);

  useEffect(() => {
    if (selectedObj) {
      const items = isInvoice
        ? selectedObj?.invoiceItems
        : selectedObj?.quoteItems;

      const date = isInvoice
        ? selectedObj?.jobScheduledFor
        : selectedObj?.dateCreated;

      const note = isInvoice
        ? selectedObj?.invoiceNote
        : selectedObj?.quoteNote;

      setQuoteDateMills(date);

      if (isInvoice && selectedObj?.jobEndTime) {
        setJobEndTimeMills(selectedObj.jobEndTime);
      }

      const docNum = isInvoice
        ? selectedObj?.invoiceNum
        : selectedObj?.quoteNum;
      setDocNum(docNum);

      setQuoteForm((prev) => ({
        ...prev,
        ...selectedObj,
        items: [...items],
        note: note,
      }));

      const existingSplit = selectedObj?.splitBetween;
      if (Array.isArray(existingSplit) && existingSplit.length > 0) {
        setSplitMin(existingSplit[0] != null ? String(existingSplit[0]) : "");
        setSplitMax(existingSplit[1] != null ? String(existingSplit[1]) : "");
      } else {
        setSplitMin("");
        setSplitMax("");
      }

      const existingSrp = selectedObj?.specialRefinisherPay;
      setSrpEnabled(!!existingSrp?.enabled);
      setSrpAmount(existingSrp?.amount || null);
      setSrpPayType(existingSrp?.payType || null);
      // Re-seeding from the record = not a user edit.
      srpDirtyRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObj, isInvoice]);

  useEffect(() => {
    const date = isInvoice
      ? selectedObj?.jobScheduledFor
      : selectedObj?.dateCreated;
    const dateArr = BAGroupTimeFormat(quoteDateMills, false, true, true)?.split(
      " "
    );
    const formattedDate = `${dateArr[0]} ${dateArr[1]}, ${dateArr[2]}`;
    const difDate = date !== quoteDateMills;

    setFormUpdates((prev) => ({ ...prev, date: difDate }));
    setQuoteDate(formattedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteDateMills]);

  useEffect(() => {
    sameItemsCheck(quoteForm?.items);

    const totalArr = quoteForm?.items?.map((item) => {
      return parseInt(
        makeTotal(
          parseInt(item?.price),
          parseInt(item?.quantity),
          item?.discountApplicable
        )
      );
    });

    const subtotalArr = quoteForm?.items?.map((item) => {
      return parseInt(
        makeTotal(parseInt(item?.price), parseInt(item?.quantity), false)
      );
    });

    const totalCheck = isInvoice
      ? selectedObj?.invoiceTotal
      : selectedObj?.quoteTotal;

    const total = totalArr?.reduce((price, price2) => price + price2, 0);
    const subtotal = subtotalArr?.reduce((price, price2) => price + price2, 0);

    const totalStr = total?.toString();
    const difPrice = totalCheck !== totalStr;

    const discountOff = subtotal - total;
    setDiscountTotal(`${discountOff}`);
    setQuoteForm((prev) => ({
      ...prev,
      quoteTotal: `${total}`,
      invoiceTotal: `${total}`,
      subtotal: `${subtotal}`,
    }));
    setFormUpdates((prev) => ({ ...prev, total: difPrice }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteForm?.items, quoteForm?.discountPercent]);

  const addQuoteItem = (newItem) => {
    if (formErrors?.length > 0) {
      clearFormErrors();
    }
    setQuoteForm((prev) => ({
      ...prev,
      items: [...prev?.items, newItem] || [newItem],
    }));
  };

  const makeTotal = (price, quantity, discount) => {
    const total = discount
      ? (
          (((100 - parseInt(quoteForm?.discountPercent)) / 100) *
            price *
            quantity) /
          100
        )
          ?.toFixed(2)
          .replace(".", "")
      : ((price * quantity) / 100)?.toFixed(2).replace(".", "");

    return total;
  };

  const sameItemsCheck = (formItems) => {
    const items = isInvoice
      ? selectedObj?.invoiceItems
      : selectedObj?.quoteItems;
    // Hardware Change refactor: include payBucket + requiresPhotos in the
    // dirty-check shape so a changed bucket flips formUpdates.items.
    const filteredItems = items?.map((obj) => {
      return {
        discountApplicable: obj?.discountApplicable,
        itemName: obj?.itemName,
        price: obj?.price,
        quantity: obj?.quantity,
        jobType: obj?.jobType ?? null,
        payBucket: obj?.payBucket ?? null,
        customPayCents: obj?.customPayCents ?? null,
        requiresPhotos:
          typeof obj?.requiresPhotos === "boolean" ? obj.requiresPhotos : null,
      };
    });

    const filteredFormItems = formItems?.map((obj) => {
      return {
        discountApplicable: obj?.discountApplicable,
        itemName: obj?.itemName,
        price: obj?.price,
        quantity: obj?.quantity,
        jobType: obj?.jobType ?? null,
        payBucket: obj?.payBucket ?? null,
        customPayCents: obj?.customPayCents ?? null,
        requiresPhotos:
          typeof obj?.requiresPhotos === "boolean" ? obj.requiresPhotos : null,
      };
    });

    const sameItems = arraysEqual(filteredItems, filteredFormItems);
    // has items been edited? have to flip sameItems boolean.
    setFormUpdates((prev) => ({ ...prev, items: !sameItems }));
  };

  const removeQuoteItem = (index) => {
    const arr = Array.from(quoteForm?.items);
    arr?.splice(index, 1);

    handleSnack("Item Removed", 2000);
    setQuoteForm((prev) => ({ ...prev, items: arr }));
  };

  const handleDateChange = (newDateMills) => {
    setQuoteDateMills(newDateMills);
  };

  const handleEndTimeChange = (newEndMills) => {
    setJobEndTimeMills(newEndMills);
  };

  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (name === "text") {
      messageInput.current?.scrollIntoView();
    }

    if (name === "discountPercent") {
      value = value.replace(/[^0-9]/gi, "");
      const difDiscount = selectedObj?.discountPercent !== value;
      setFormUpdates((prev) => ({ ...prev, discount: difDiscount }));
    }

    setQuoteForm((prev) => ({ ...prev, [name]: value }));
  };

  const clearFormErrors = () => {
    setFormErrors([]);
  };

  // periodConfirmed is only true when the pay-period confirm modal retries.
  const updateCall = async (periodConfirmed) => {
    const valid = await quoteValidator.updateValid();
    if (valid !== true) {
      // Hard errors block outright, exactly as before the hard/soft split.
      if (valid.hard?.length > 0) {
        setFormErrors([...valid.hard]);
        return;
      }
      // Soft-only = $0 money values (warranty / no-charge job). Confirm
      // instead of block — but not on the pay-period-guard retry, which
      // re-enters this function after the user already confirmed the $0
      // save in the same gesture.
      if (periodConfirmed !== true) {
        const docLabel = isInvoice ? "invoice" : "quote";
        const zeroTotal = parseInt(quoteForm?.invoiceTotal) === 0;
        const ok = await confirm({
          title: zeroTotal ? "Save with $0.00 total?" : "$0.00 line item",
          message: zeroTotal
            ? `This will save the ${docLabel} with a total of $0.00. (Warranty / no-charge job — the refinisher can still be paid via Special Refinisher Pay.)`
            : `This ${docLabel} contains item(s) priced at $0.00. Save anyway?`,
          confirmLabel: "Save",
        });
        if (!ok) return;
      }
    }

    const filteredFormItems = quoteForm?.items?.map((obj) => {
      return {
        discountApplicable: obj?.discountApplicable,
        itemName: obj?.itemName,
        price: obj?.price,
        quantity: obj?.quantity,
        jobType: obj?.jobType ?? null,
        // Hardware Change refactor: forward payBucket + requiresPhotos to
        // the server. Without this, the update mutation strips them and
        // the next pay calc routes the line item via the regex fallback
        // (refinish-by-default). Critical for HC items where the catalog
        // sets payBucket='full' but the snapshot must carry it through.
        payBucket: obj?.payBucket ?? null,
        // 'custom' bucket amount rides along (2026-10-06); omitting it here
        // would strip the snapshot on every edit, just like payBucket.
        customPayCents: obj?.customPayCents ?? null,
        requiresPhotos:
          typeof obj?.requiresPhotos === "boolean" ? obj.requiresPhotos : null,
      };
    });

    const minVal = parseInt(splitMin);
    const maxVal = parseInt(splitMax);
    let splitBetweenArr = [];
    if (!isNaN(minVal) && !isNaN(maxVal)) {
      splitBetweenArr = [minVal, maxVal];
    } else if (!isNaN(minVal)) {
      splitBetweenArr = [minVal];
    }

    const updateObj = {
      id: quoteForm?.id,
      isInvoice: isInvoice,
      discountPercent: quoteForm?.discountPercent,
      subtotal: quoteForm?.subtotal,
      total: quoteForm?.invoiceTotal,
      dateMills: quoteDateMills,
      jobEndTime: isInvoice ? jobEndTimeMills : undefined,
      itemsArr: [...filteredFormItems],
      note: quoteForm?.note,
      splitBetween: splitBetweenArr,
    };
    // Dirty-gate: only send specialRefinisherPay when the user touched the
    // SRP block this session. Omitting it preserves the stored value server-
    // side, so a stale seed can't wipe an override set on the calendar event.
    if (srpDirtyRef.current) {
      const srpValidationError = validateSpecialRefinisherPay(srpEnabled, srpAmount);
      if (srpValidationError) {
        window.alert(srpValidationError);
        return;
      }
      updateObj.specialRefinisherPay = buildSpecialRefinisherPayInput(
        srpEnabled,
        srpAmount,
        srpPayType
      );
    }
    // Pay-period guard retry: set only when the confirm modal re-invoked save.
    if (periodConfirmed === true) updateObj.confirmPeriodMove = true;

    try {
      const { data } = await updateGQLCall({
        variables: { ...updateObj },
      }, {fetchPolicy: "network-only"});

      if (data?.updateQuoteInvoice) {
        const newObj = isInvoice
          ? data?.updateQuoteInvoice?.invoice
          : data?.updateQuoteInvoice?.quote;

        if (handleSnack) {
          const text = isInvoice ? "Updated Invoice" : "Updated Quote";
          handleSnack(text, 2500);
        }
        setPeriodMove(null);
        returnNewObj(newObj, isInvoice);
      }
    } catch (err) {
      // Editing the invoice date would re-bucket an approved job's paycheck
      // across a settled pay period — confirm + retry.
      if (isNeedsPeriodConfirm(err)) {
        setPeriodMove({
          oldMs: selectedObj?.jobScheduledFor,
          newMs: quoteDateMills,
          onConfirm: () => updateCall(true),
        });
        return;
      }
      console.error("[QuoteInvoiceEdit] update failed:", err?.message || err);
      if (handleSnack) handleSnack(err?.message || "Update failed", 3500);
    }
  };

  return quoteForm ? (
    <div className="cq-v2-content">
      {/* Header */}
      <div className="cq-v2-header">
        <h3 className="cq-v2-title">
          {isInvoice ? "Edit Invoice" : "Edit Quote"}
        </h3>
        <div className="cq-v2-header-meta">
          <span className="cq-v2-quote-num">
            {isInvoice ? `Invoice #${docNum}` : `Quote #${docNum}`}
          </span>
          <IconButton onClick={closeModal} size="small" sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      {/* Client Info Bar */}
      <div className="cq-v2-client-bar">
        <span className="cq-v2-field-label">Client</span>
        <span className="cq-v2-client-name">
          {capitalizeFirstLetters(quoteForm?.customerName)}
        </span>
      </div>

      {/* Config Row: Status (quote-only, disabled), Discount */}
      <div className="cq-v2-config-row">
        {!isInvoice && (
          <div className="cq-v2-select-group">
            <span className="cq-v2-field-label">Status</span>
            <BdrSelectPopup
              id="eq-quote-status"
              className="bdr-input bdr-select-trigger"
              name="quoteStatus"
              value={quoteForm?.quoteStatus}
              disabled
              options={QUOTE_STATUS_OPTIONS}
            />
          </div>
        )}
        <div className={`cq-v2-select-group${formUpdates?.discount ? " eq-v2-field-updated" : ""}`}>
          <span className="cq-v2-field-label">Discount %</span>
          <BdrSelectPopup
            id="eq-discount-percent"
            className="bdr-input bdr-select-trigger"
            name="discountPercent"
            value={quoteForm?.discountPercent}
            onChange={handleChange}
            options={DISCOUNT_PERCENT_OPTIONS}
          />
        </div>
      </div>

      {/* Refinishers Row */}
      <div className="cq-v2-refinishers-row">
        <span className="cq-v2-field-label">Refinishers</span>
        <div className="cq-v2-refinisher-inputs">
          <NumberStepper
            label="Min"
            value={splitMin}
            onChange={(val) => setSplitMin(String(val))}
            min={1}
            max={99}
          />
          <NumberStepper
            label="Max"
            value={splitMax}
            onChange={(val) => setSplitMax(String(val))}
            min={1}
            max={99}
          />
        </div>
      </div>

      {/* Special Refinisher Pay override */}
      <div className="cq-v2-refinishers-row">
        <SpecialRefinisherPayBlock
          enabled={srpEnabled}
          setEnabled={(v) => {
            srpDirtyRef.current = true;
            setSrpEnabled(v);
          }}
          amount={srpAmount}
          setAmount={(v) => {
            srpDirtyRef.current = true;
            setSrpAmount(v);
          }}
          payType={srpPayType}
          setPayType={(v) => {
            srpDirtyRef.current = true;
            setSrpPayType(v);
          }}
          theme="dark"
          items={quoteForm?.items}
        />
      </div>

      {/* Dates Row */}
      <div className="cq-v2-dates-row">
        <div className={`cq-v2-date-field${formUpdates?.date ? " eq-v2-field-updated" : ""}`}>
          {isInvoice ? (
            <>
              <EventDateTimeInput
                value={quoteDateMills}
                onChange={handleDateChange}
                label="Start Time:"
              />
              <EventDateTimeInput
                value={jobEndTimeMills}
                onChange={handleEndTimeChange}
                label="End Time:"
              />
            </>
          ) : (
            <>
              <span className="cq-v2-field-label">Quote Date:</span>
              <span className="cq-v2-date-readonly">{quoteDate}</span>
            </>
          )}
        </div>
        {!isInvoice && (
          <div className="cq-v2-date-field">
            <span className="cq-v2-field-label">Expires:</span>
            <span className="cq-v2-date-readonly">
              {BAGroupTimeFormat(quoteForm?.expirationDate, false, true)}
            </span>
          </div>
        )}
      </div>

      {/* Error Messages */}
      {formErrors?.length > 0 && (
        <div className="cq-v2-errors">
          <ul>
            {formErrors.map((err, i) => (
              <li key={err + i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Items Section */}
      <div className={`cq-v2-items-section${formUpdates?.items ? " eq-v2-field-updated" : ""}`}>
        <span className="cq-v2-section-label">
          {isInvoice ? "Invoice Items" : "Quote Items"}
        </span>

        {/* Add Item Controls */}
        <div className="cq-v2-add-item-box">
          <NewQuoteItemBox
            discountPercent={parseInt(quoteForm?.discountPercent)}
            addQuoteItem={addQuoteItem}
            handleSnack={handleSnack}
          />
        </div>

        {/* Items List */}
        {quoteForm?.items?.length > 0 && (
          <div className="cq-v2-items-list">
            <div className="cq-v2-items-header">
              <span className="cq-v2-ih-name">Item</span>
              <span className="cq-v2-ih-price">Price</span>
              <span className="cq-v2-ih-qty">Qty</span>
              <span className="cq-v2-ih-disc">Disc.</span>
              <span className="cq-v2-ih-total">Total</span>
              <span className="cq-v2-ih-action"></span>
            </div>
            {quoteForm.items.map((item, index) => {
              const total = makeTotal(
                parseInt(item?.price),
                parseInt(item?.quantity),
                item?.discountApplicable
              );
              return (
                <div key={`cq-v2-item-${index}`} className="cq-v2-item-row">
                  <span className="cq-v2-ir-name">{item?.itemName}</span>
                  <span className="cq-v2-ir-price">{priceStrFormatter(item?.price)}</span>
                  <span className="cq-v2-ir-qty">{item?.quantity}</span>
                  <span className="cq-v2-ir-disc">
                    {item?.discountApplicable
                      ? quoteForm?.discountPercent === "0" ? "N/A" : `${quoteForm.discountPercent}%`
                      : "N/A"}
                  </span>
                  <span className="cq-v2-ir-total">{priceStrFormatter(total)}</span>
                  <span className="cq-v2-ir-action">
                    {/* plain + sm: an icon action in a dense row (§6b), and
                        the same control as CreateQuoteModal's, which this
                        modal sits beside in the same flow. */}
                    <BdrButton
                      variant="plain"
                      size="sm"
                      onClick={() => removeQuoteItem(index)}
                      ariaLabel={`Remove ${item?.itemName || "item"}`}
                      title="Remove item"
                    >
                      ×
                    </BdrButton>
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Totals */}
        <div className={`cq-v2-totals${formUpdates?.total ? " eq-v2-field-updated" : ""}`}>
          <div className="cq-v2-total-row">
            <span>Subtotal</span>
            <span>{priceStrFormatter(quoteForm?.subtotal)}</span>
          </div>
          {discountTotal > 0 && (
            <div className="cq-v2-total-row cq-v2-discount-row">
              <span>Discount</span>
              <span>- {priceStrFormatter(discountTotal)}</span>
            </div>
          )}
          <div className="cq-v2-total-row cq-v2-grand-total">
            <strong>{isInvoice ? "Invoice Total" : "Quote Total"}</strong>
            <strong>{priceStrFormatter(quoteForm?.quoteTotal)}</strong>
          </div>
        </div>
      </div>

      {/* Note Field */}
      <div className="cq-v2-field">
        <BdrInput
          
          inputRef={messageInput}
          label={isInvoice ? "Invoice Note (optional)" : "Quote Note (optional)"}
          name="note"
          value={quoteForm?.note}
          onChange={handleChange}
          multiline
          rows={3}
        />
      </div>

      {/* Action Buttons */}
      <div className="cq-v2-actions">
        <BdrButton onClick={closeModal}>Cancel</BdrButton>
        {/* solid: the host modal is variant="panel", the saturated
            --bdr-grad-panel, where a tinted fill muddies in (§6b). */}
        <BdrButton
          name="saveOnly"
          variant="confirm-solid"
          onClick={() => {
            clearFormErrors();
            updateCall();
          }}
        >
          {isInvoice ? "Update Invoice" : "Update Quote"}
        </BdrButton>
      </div>
      <PayPeriodMoveConfirmModal
        open={!!periodMove}
        oldMs={periodMove?.oldMs}
        newMs={periodMove?.newMs}
        onCancel={() => setPeriodMove(null)}
        onConfirm={() => periodMove?.onConfirm?.()}
      />
    </div>
  ) : (
    <></>
  );
};

export default QuoteInvoiceEditPage;
